import { useState } from 'react';
import { VStack, Text, useBreakpointValue } from '@skin-seoul/design-system';
import {
  useUser,
  transformOrderForDisplay,
  ORDER_QUERIES,
  useReviewService,
  type ReviewFormData,
} from '@skin-seoul/service';
import {
  OrderCard,
  OrderCardSkeleton,
  LucideIcon,
  ReviewWritePcModal,
  ReviewWriteMobileModal,
  type OrderItem,
} from '@skin-seoul/shared-components';
import { useCallbackRef } from '@skin-seoul/utils';
import { useQuery } from '@tanstack/react-query';

interface OrderItemProps {
  orderId: string;
  onClickWriteReview: (item: OrderItem) => void;
}

const OrderItemCard = ({ orderId, onClickWriteReview }: OrderItemProps) => {
  const { data: order, isLoading, isError } = useQuery(ORDER_QUERIES.getOrder({ orderId: Number(orderId) }));

  if (isLoading) {
    return <OrderCardSkeleton />;
  }

  if (isError || !order) {
    return null;
  }

  return <OrderCard order={transformOrderForDisplay(order)} onClickWriteReview={onClickWriteReview} />;
};

const RegisteredDeliveryTracking = () => {
  const { user, isLoading: isUserLoading, isAuthenticated } = useUser();
  const isPC = useBreakpointValue({ base: false, tablet: true });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<OrderItem | null>(null);

  const { useCreateReview } = useReviewService();
  const { mutate: createReview } = useCreateReview();

  const { data: userOrders, isLoading: isOrdersLoading } = useQuery({
    ...ORDER_QUERIES.getUserOrders({ userId: Number(user?.userId ?? 0) }),
    enabled: isAuthenticated && Boolean(user?.userId),
  });

  const handleSubmitReview = useCallbackRef((values: ReviewFormData) => {
    return new Promise<void>((resolve, reject) => {
      if (!selectedItem?.productId) {
        reject(new Error('Product ID is required'));
        return;
      }

      createReview(
        {
          name: values.nickname,
          rating: values.rating,
          body: values.content,
          productId: Number(selectedItem.productId),
          title: values.title,
          files: values.images,
          skinType: [values.skinType],
          skinConcern: [values.skinConcern],
          email: values.email,
        },
        {
          onSuccess: () => {
            resolve();
            setIsModalOpen(false);
            setSelectedItem(null);
          },
          onError: (error) => {
            reject(new Error('Failed to create review: ' + (error?.message || 'Unknown error')));
          },
        },
      );
    });
  });

  const handleClickWriteReview = useCallbackRef((item: OrderItem) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  });

  const handleCloseModal = useCallbackRef(() => {
    setIsModalOpen(false);
    setSelectedItem(null);
  });

  if (isUserLoading || isOrdersLoading) {
    return (
      <VStack width="100%" gap={4} padding={4}>
        <OrderCardSkeleton />
        <OrderCardSkeleton />
      </VStack>
    );
  }

  const hasOrders = userOrders?.orderIds && userOrders.orderIds.length > 0;

  if (!hasOrders) {
    return (
      <VStack
        width="100%"
        bg="generic.0"
        border="1px solid"
        borderColor="neutral.100"
        borderRadius="8px"
        padding={8}
        gap={3}
        alignItems="center"
        justifyContent="center"
        minHeight="200px"
      >
        <LucideIcon name="Box" width={16} height={16} color="neutral.400" />
        <Text textStyle="heading.h5.mobile" fontWeight="semibold" color="neutral.700">
          No Orders Found
        </Text>
        <Text textStyle="paragraph.medium" color="neutral.400" textAlign="center">
          You haven&apos;t placed any orders yet.
        </Text>
      </VStack>
    );
  }

  const productInfo = selectedItem
    ? {
        name: selectedItem.name,
        brand: selectedItem.brand ?? '',
        image: selectedItem.imageUrl ?? '',
        category: selectedItem.category ?? '',
      }
    : null;

  return (
    <>
      <VStack width="100%" gap={4}>
        <VStack width="100%" gap={2} alignItems={{ base: 'center', tablet: 'flex-start' }}>
          <Text
            textStyle="heading.h6.mobile"
            fontWeight="medium"
            color="neutral.900"
            textAlign={{ base: 'center', tablet: 'left' }}
          >
            Orders
          </Text>
          <Text
            textStyle="paragraph.small"
            fontWeight="regular"
            color="neutral.400"
            textAlign={{ base: 'center', tablet: 'left' }}
          >
            Check your order and delivery status.
          </Text>
        </VStack>

        <VStack width="100%" gap={4}>
          {userOrders.orderIds.map((orderId) => (
            <OrderItemCard key={orderId} orderId={orderId} onClickWriteReview={handleClickWriteReview} />
          ))}
        </VStack>
      </VStack>

      {productInfo &&
        (isPC ? (
          <ReviewWritePcModal
            isOpen={isModalOpen}
            productInfo={productInfo}
            onClose={handleCloseModal}
            onSubmit={handleSubmitReview}
          />
        ) : (
          <ReviewWriteMobileModal
            isOpen={isModalOpen}
            productInfo={productInfo}
            onClose={handleCloseModal}
            onSubmit={handleSubmitReview}
          />
        ))}
    </>
  );
};

export default RegisteredDeliveryTracking;
